import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";

import { Typography } from "@/components/Typography";
import type { TransactionFilters } from "@/pages/billing/v2/tabs/transactionHistory/utils";
import {
  getMaxDate,
  getMinDate,
} from "@/pages/billing/v2/tabs/transactionHistory/utils";

const styles = {
  card: {
    border: `1px solid ${theme.colors.neutral020}`,
    borderRadius: "12px",
  },
  cardContent: {
    padding: "16px 20px",
    "&:last-child": {
      paddingBottom: "16px",
    },
  },
  dateInput: {
    width: "180px",
    "& .MuiInputBase-input": {
      padding: "8px 12px",
      fontSize: "0.875rem",
    },
  },
  separator: {
    display: "flex",
    alignItems: "center",
    color: theme.colors.neutral050,
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface TransactionFilterBarProps {
  filters: TransactionFilters;
  onFiltersChange: (filters: TransactionFilters) => void;
}

export const TransactionFilterBar: React.FC<TransactionFilterBarProps> =
  function TransactionFilterBar({ filters, onFiltersChange }) {
    const minDate = useMemo(() => getMinDate(), []);
    const maxDate = useMemo(() => getMaxDate(), []);

    const handleDateFromChange = useHandler<
      React.ComponentProps<typeof TextField>["onChange"]
    >((e) => {
      if (!e.target.value) return;
      onFiltersChange({
        ...filters,
        dateFrom: e.target.value,
      });
    });

    const handleDateToChange = useHandler<
      React.ComponentProps<typeof TextField>["onChange"]
    >((e) => {
      if (!e.target.value) return;
      onFiltersChange({
        ...filters,
        dateTo: e.target.value,
      });
    });

    return (
      <Card variant="outlined" sx={styles.card}>
        <CardContent sx={styles.cardContent}>
          <Stack direction="row" alignItems="center" gap="12px">
            <TextField
              type="date"
              size="small"
              value={filters.dateFrom}
              onChange={handleDateFromChange}
              sx={styles.dateInput}
              slotProps={{
                htmlInput: {
                  min: minDate,
                  max: filters.dateTo,
                },
              }}
            />
            <Box sx={styles.separator}>
              <Typography variant="body">~</Typography>
            </Box>
            <TextField
              type="date"
              size="small"
              value={filters.dateTo}
              onChange={handleDateToChange}
              sx={styles.dateInput}
              slotProps={{
                htmlInput: {
                  min: filters.dateFrom,
                  max: maxDate,
                },
              }}
            />
          </Stack>
        </CardContent>
      </Card>
    );
  };
